import { ApolloLink, type FieldPolicy } from "@apollo/client";
import { map } from "rxjs";
import { scalarTypePolicies } from "src/generated/graphqlTypes";

/** Converts a raw scalar value (or scalar array) into its Temporal representation. */
type ScalarConverter = (value: unknown) => unknown;

/** The subset of a scalar field policy's `merge` signature this link relies on. */
type ScalarMergeFn = (existing: unknown, incoming: unknown, options: unknown) => unknown;

/** `__typename` -> field name -> converter, derived once from the generated scalar type policies. */
const scalarFieldsByType = buildScalarFieldMap();

/**
 * Parses `Date`/`DateTime` scalar strings into Temporal values directly on the network response.
 *
 * Our scalar type policies (`src/utils/datePolicies.ts`) convert via cache `merge`, i.e. at cache-write
 * time, so components only receive Temporal values when Apollo surfaces the value it read back from the
 * cache. Apollo v4 instead surfaces the raw link result whenever a query's post-write cache read-back is
 * incomplete (see `QueryInfo.markQueryResult`'s `diff.complete` gate), which skips that conversion and
 * leaks raw strings — e.g. `date.toPT is not a function`. Transforming the response here makes the raw
 * result correct too, so every delivery path (network, cache, no-cache, mutations) yields Temporal values.
 * The cache `merge` policies remain as an idempotent backstop (they no-op on Temporal input).
 */
export const scalarTransformLink = new ApolloLink((operation, forward) =>
  forward(operation).pipe(
    map((result) => {
      if (result.data) transformScalars(result.data);
      return result;
    }),
  ),
);

/** Recursively walks a response value, converting configured scalar fields to Temporal in place. */
export function transformScalars(value: unknown): void {
  if (value === null || typeof value !== "object") return;
  if (Array.isArray(value)) {
    for (const item of value) transformScalars(item);
    return;
  }
  const obj = value as Record<string, unknown>;
  const converters = typeof obj.__typename === "string" ? scalarFieldsByType.get(obj.__typename) : undefined;
  for (const key of Object.keys(obj)) {
    const converter = converters?.get(key);
    if (converter) {
      // A configured scalar (or scalar-array) leaf field: convert it, don't recurse into its value.
      if (obj[key] != null) obj[key] = converter(obj[key]);
    } else {
      transformScalars(obj[key]);
    }
  }
}

/** Derives the `__typename` -> field -> converter map from the generated scalar type policies. */
function buildScalarFieldMap(): Map<string, Map<string, ScalarConverter>> {
  const byType = new Map<string, Map<string, ScalarConverter>>();
  for (const [typename, policy] of Object.entries<{ fields?: Record<string, FieldPolicy> }>(scalarTypePolicies)) {
    if (!policy.fields) continue;
    const fields = new Map<string, ScalarConverter>();
    for (const [field, fieldPolicy] of Object.entries(policy.fields)) {
      const { merge } = fieldPolicy;
      // Reuse the policy's own string->Temporal conversion. Our Date/DateTime merges are pure w.r.t. the
      // `existing`/`options` args (they only read `incoming`), so passing stubs for them is safe.
      if (typeof merge === "function") {
        const convert = merge as unknown as ScalarMergeFn;
        fields.set(field, (value) => convert(undefined, value, undefined));
      }
    }
    if (fields.size > 0) byType.set(typename, fields);
  }
  return byType;
}
